import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Box, useTheme, Button, ButtonGroup, Typography, IconButton, Tooltip, Chip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { DuplicateWeekConfirmationDialog } from "./PatrolModal/dialogs";
import { getCalendarStyles } from "./Calendar.styles";
import {
  SHIFT_COLORS,
  BREAK_VISUAL_OFFSET_MINUTES,
  SHIFT_GAP_THRESHOLD_MINUTES,
  CALENDAR_ZOOM_MIN,
  CALENDAR_ZOOM_MAX,
  CALENDAR_ZOOM_STEP,
  CALENDAR_ZOOM_DEFAULT,
} from "./constants";

// Configure moment to start weeks on Monday
moment.updateLocale("en", {
  week: {
    dow: 1, // Monday is the first day of the week
  },
});

const localizer = momentLocalizer(moment);

// Custom Week Day Header Component factory
// Returns a component that uses the provided isMobile flag
const createWeekDayHeader = (isMobile) => {
  // Note: localizer is passed by react-big-calendar but not needed for our custom rendering
  const WeekDayHeader = ({ date }) => {
    const dayName = moment(date).format("ddd"); // Mon, Tue, etc.
    const dateStr = moment(date).format("MMM D"); // Jan 19, Feb 3, etc.
    const isToday = moment(date).isSame(moment(), "day");

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "6px 0" : "12px 0",
          gap: isMobile ? 0.25 : 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? "0.6rem" : "0.75rem",
            fontWeight: 500,
            color: isToday ? "primary.main" : "text.secondary",
            textTransform: "uppercase",
            letterSpacing: isMobile ? "0.25px" : "0.5px",
          }}
        >
          {dayName}
        </Typography>
        <Typography
          sx={{
            fontSize: isMobile ? "0.7rem" : "1rem",
            fontWeight: 600,
            color: isToday ? "primary.main" : "text.primary",
          }}
        >
          {dateStr}
        </Typography>
      </Box>
    );
  };
  return WeekDayHeader;
};


// Custom Event Component for displaying patrol info with edit button
const CustomEvent = ({ event, onEditClick, viewMode, isMobile }) => {
  // Don't show custom styling for break events
  if (event.isBreak) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontStyle: "italic",
        }}
      >
      </Box>
    );
  }

  const location = event.location || event.title?.split(" - ")[1] || "";

  const handleEditClick = (e) => {
    e.stopPropagation(); // Prevent event selection
    onEditClick?.(event);
  };

  // Mobile compact mode - just show colored blocks, minimal text
  if (isMobile) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: "2px",
        }}
      >
        {/* Location - truncated */}
        <Box
          sx={{
            fontWeight: 600,
            fontSize: "0.65rem",
            lineHeight: 1.1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {location}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: "4px 0",
        // Use container query for responsive behavior
        containerType: "inline-size",
      }}
    >
      {/* Row 1: Location with icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          fontWeight: 600,
          fontSize: "0.85rem",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "@container (max-width: 70px)": {
            fontSize: "0.75rem",
          },
        }}
      >
        <LocationOnIcon
          sx={{
            fontSize: 14,
            flexShrink: 0,
            "@container (max-width: 70px)": {
              display: "none",
            },
          }}
        />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {location}
        </span>
      </Box>

      {/* Row 2: Guard/Company chip */}
      {viewMode !== 'guard' && (
        <Box
          sx={{
            mt: 0.5,
            "@container (max-width: 70px)": {
              display: "none",
            },
          }}
        >
          <Chip
            icon={<PersonIcon sx={{ fontSize: 14 }} />}
            label={event.resource}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              backgroundColor: "transparent",
              borderColor: "currentColor",
              color: "inherit",
              "& .MuiChip-label": {
                px: 0.75,
              },
              "& .MuiChip-icon": {
                ml: 0.5,
                color: "inherit",
              },
            }}
          />
        </Box>
      )}

      {viewMode === 'guard' && event.patrol?.CustomerName && (
        <Box
          sx={{
            mt: 0.5,
            "@container (max-width: 70px)": {
              display: "none",
            },
          }}
        >
          <Chip
            icon={<BusinessIcon sx={{ fontSize: 14 }} />}
            label={event.patrol.CustomerName}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              backgroundColor: "transparent",
              borderColor: "currentColor",
              color: "inherit",
              "& .MuiChip-label": {
                px: 0.75,
              },
              "& .MuiChip-icon": {
                ml: 0.5,
                color: "inherit",
              },
            }}
          />
        </Box>
      )}

      {/* Row 3: Edit button */}
      <Box
        sx={{
          mt: 0.5,
          "@container (max-width: 70px)": {
            display: "none",
          },
        }}
      >
        <Tooltip title="Edit patrol" placement="top">
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{
              padding: "2px",
              opacity: 0.8,
              backgroundColor: "transparent",
              border: "1px solid currentColor",
              "&:hover": {
                opacity: 1,
                backgroundColor: "rgba(255,255,255,0.3)",
              },
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Narrow width layout - stacked content */}
      <Box
        sx={{
          display: "none",
          "@container (max-width: 70px)": {
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            mt: 0.5,
          },
        }}
      >
        {/* Guard/Company name as text */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            fontSize: "0.6rem",
            opacity: 0.9,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {viewMode === 'guard' ? (
            <>
              <BusinessIcon sx={{ fontSize: 10, flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {event.patrol?.CustomerName}
              </span>
            </>
          ) : (
            <>
              <PersonIcon sx={{ fontSize: 10, flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {event.resource}
              </span>
            </>
          )}
        </Box>

        {/* Edit button */}
        <Tooltip title="Edit patrol" placement="top">
          <IconButton
            size="small"
            onClick={handleEditClick}
            sx={{
              alignSelf: "flex-start",
              padding: "1px",
              opacity: 0.8,
              backgroundColor: "transparent",
              border: "1px solid currentColor",
              "&:hover": {
                opacity: 1,
                backgroundColor: "rgba(255,255,255,0.3)",
              },
            }}
          >
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const SchedulerCalendar = ({
  patrols = [],
  shifts = [],
  viewMode = 'company',
  selectedCompanyId,
  selectedLocationId,
  selectedGuardId,
  selectedCompanyName,
  selectedLocationName,
  selectedGuardName,
  onWeekChange,
  onEventClick,
  onEditPatrol,
  onRegisterNavigate,
  onDuplicateWeek,
  isMobile = false,
}) => {
  const theme = useTheme();

  // Filter patrols based on view mode
  // Company View: Filter by company (required) and optionally by location
  // Guard View: Filter by selected guard across ALL companies/locations
  const filteredPatrols = useMemo(() => {
    if (viewMode === 'guard') {
      // Guard View: Show all patrols for selected guard across all companies
      if (!selectedGuardId) return [];
      return patrols.filter((patrol) => patrol.ContactId === selectedGuardId);
    }

    // Company View: Filter by company (required) and optionally by location
    if (!selectedCompanyId) return [];
    return patrols.filter((patrol) => {
      const matchesCompany = patrol.CustomerId === selectedCompanyId;
      const matchesLocation = !selectedLocationId || patrol.AddressId === selectedLocationId;
      return matchesCompany && matchesLocation;
    });
  }, [patrols, viewMode, selectedCompanyId, selectedLocationId, selectedGuardId]);

  // Determine if we have a required selection based on view mode
  const hasRequiredSelection = viewMode === 'company' ? selectedCompanyId : selectedGuardId;

  // Create a mapping of patrol ID to shift ID and color index
  const patrolToShiftMap = useMemo(() => {
    const map = new Map();
    shifts.forEach((shift, shiftIndex) => {
      const colorIndex = shiftIndex % SHIFT_COLORS.length;
      shift.patrols.forEach((patrol) => {
        map.set(patrol.ScheduleAssignmentId, { shiftId: shift.id, colorIndex });
      });
    });
    return map;
  }, [shifts]);

  // Helper function to split events that span across midnight into multiple day segments
  const splitOvernightEvents = useCallback((patrol) => {
    const start = moment(patrol.StartTime);
    const end = moment(patrol.EndTime);
    const events = [];

    // Get the color index for this patrol's shift
    const shiftInfo = patrolToShiftMap.get(patrol.ScheduleAssignmentId);
    const colorIndex = shiftInfo?.colorIndex ?? 0;

    // If event doesn't span midnight, return single event
    if (start.isSame(end, "day")) {
      events.push({
        id: patrol.ScheduleAssignmentId,
        patrolId: patrol.ScheduleAssignmentId, // Original patrol ID for edit
        title: `${patrol.ContactName} - ${patrol.AddressName}`,
        location: patrol.AddressName,
        start: patrol.StartTime,
        end: patrol.EndTime,
        resource: patrol.ContactName,
        originalStart: patrol.StartTime, // Keep original for click handler
        colorIndex,
        patrol, // Keep reference to original patrol for edit
      });
      return events;
    }

    // Event spans multiple days - split into segments
    let currentStart = start.clone();

    while (!currentStart.isSame(end, "day")) {
      // End of current day (23:59:59)
      const dayEnd = currentStart.clone().endOf("day").toDate();

      events.push({
        id: `${patrol.ScheduleAssignmentId}-${currentStart.format("YYYY-MM-DD")}`,
        patrolId: patrol.ScheduleAssignmentId, // Original patrol ID for edit
        title: `${patrol.ContactName} - ${patrol.AddressName}`,
        location: patrol.AddressName,
        start: currentStart.toDate(),
        end: dayEnd,
        resource: patrol.ContactName,
        originalStart: patrol.StartTime, // Keep original for click handler
        isSplit: true,
        isFirstPart: events.length === 0,
        colorIndex,
        patrol, // Keep reference to original patrol for edit
      });

      // Move to start of next day
      currentStart = currentStart.clone().add(1, "day").startOf("day");
    }

    // Add the final segment (from midnight to actual end time)
    if (currentStart.isBefore(end)) {
      events.push({
        id: `${patrol.ScheduleAssignmentId}-${currentStart.format("YYYY-MM-DD")}`,
        patrolId: patrol.ScheduleAssignmentId, // Original patrol ID for edit
        title: `${patrol.ContactName} - ${patrol.AddressName}`,
        location: patrol.AddressName,
        start: currentStart.toDate(),
        end: patrol.EndTime,
        resource: patrol.ContactName,
        originalStart: patrol.StartTime, // Keep original for click handler
        isSplit: true,
        isFirstPart: false,
        colorIndex,
        patrol, // Keep reference to original patrol for edit
      });
    }

    return events;
  }, [patrolToShiftMap]);

  // Generate break events for gaps between patrols in the same shift (< 60 minutes)
  const generateBreakEvents = useCallback(() => {
    const breakEvents = [];
    const filteredPatrolIds = new Set(filteredPatrols.map((p) => p.ScheduleAssignmentId));

    shifts.forEach((shift) => {
      // Only process shifts that have patrols visible in the current filter
      const visiblePatrols = shift.patrols.filter((p) => filteredPatrolIds.has(p.ScheduleAssignmentId));
      if (visiblePatrols.length < 2) return;

      // Sort patrols by start time
      const sortedPatrols = [...visiblePatrols].sort(
        (a, b) => moment(a.StartTime).diff(moment(b.StartTime))
      );

      // Find gaps between consecutive patrols
      for (let i = 0; i < sortedPatrols.length - 1; i++) {
        const currentPatrol = sortedPatrols[i];
        const nextPatrol = sortedPatrols[i + 1];

        const gapStart = moment(currentPatrol.EndTime);
        const gapEnd = moment(nextPatrol.StartTime);
        const gapMinutes = gapEnd.diff(gapStart, "minutes");

        // Only create break events for gaps less than threshold and greater than 0
        if (gapMinutes > 0 && gapMinutes < SHIFT_GAP_THRESHOLD_MINUTES) {
          // Subtract offset from visual end time to prevent overlap with next patrol
          const visualEnd = gapEnd.clone().subtract(BREAK_VISUAL_OFFSET_MINUTES, "minute");
          breakEvents.push({
            id: `break-${currentPatrol.ScheduleAssignmentId}-${nextPatrol.ScheduleAssignmentId}`,
            title: "Break",
            start: gapStart.toDate(),
            end: visualEnd.toDate(),
            resource: shift.ContactName,
            isBreak: true,
          });
        }
      }
    });

    return breakEvents;
  }, [shifts, filteredPatrols]);

  // Transform patrol data to calendar event format, splitting overnight events
  // Also include break events
  const events = useMemo(() => {
    const patrolEvents = filteredPatrols.flatMap((patrol) => splitOvernightEvents(patrol));
    const breakEvents = generateBreakEvents();
    return [...patrolEvents, ...breakEvents];
  }, [filteredPatrols, splitOvernightEvents, generateBreakEvents]);

  // Helper function to get the Monday of a given week
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    d.setDate(diff);
    return d;
  };

  // Helper function to get the Sunday of a given week
  const getSundayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? 0 : 7); // Adjust for Sunday
    d.setDate(diff);
    return d;
  };

  // Initialize state with current week
  const [currentDate, setCurrentDate] = useState(() => {
    const today = new Date();
    return getMondayOfWeek(today);
  });

  // Zoom state - lower default on mobile for better week overview
  const [zoomLevel, setZoomLevel] = useState(isMobile ? CALENDAR_ZOOM_MIN : CALENDAR_ZOOM_DEFAULT);

  // Duplicate confirmation dialog state
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

  // Get calendar styles with current zoom level and mobile flag
  const calendarStyles = useMemo(() => {
    return getCalendarStyles(theme, zoomLevel, isMobile);
  }, [theme, zoomLevel, isMobile]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + CALENDAR_ZOOM_STEP, CALENDAR_ZOOM_MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - CALENDAR_ZOOM_STEP, CALENDAR_ZOOM_MIN));
  }, []);

  const canZoomIn = zoomLevel < CALENDAR_ZOOM_MAX;
  const canZoomOut = zoomLevel > CALENDAR_ZOOM_MIN;

  const weekStart = currentDate;
  // Memoize weekEnd to prevent creating new Date objects on every render
  // This prevents the useEffect from firing continuously
  const weekEnd = useMemo(() => getSundayOfWeek(new Date(currentDate)), [currentDate]);

  // Check if there are any patrols in the current week
  const hasPatrolsThisWeek = useMemo(() => {
    const weekStartMoment = moment(weekStart).startOf("day");
    const weekEndMoment = moment(weekEnd).endOf("day");

    return filteredPatrols.some((patrol) => {
      const patrolStart = moment(patrol.StartTime);
      const patrolEnd = moment(patrol.EndTime);
      // Check if patrol overlaps with the current week
      return patrolStart.isSameOrBefore(weekEndMoment) && patrolEnd.isSameOrAfter(weekStartMoment);
    });
  }, [filteredPatrols, weekStart, weekEnd]);

  // Call parent callback when dates change
  useEffect(() => {
    if (onWeekChange) {
      onWeekChange(weekStart, weekEnd);
    }
  }, [weekStart, weekEnd, onWeekChange]);

  // Register navigation function with parent
  useEffect(() => {
    if (onRegisterNavigate) {
      onRegisterNavigate((date) => {
        const monday = getMondayOfWeek(date);
        setCurrentDate(monday);
      });
    }
  }, [onRegisterNavigate]);

  const handlePreviousWeek = useCallback(() => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  }, []);

  const handleNextWeek = useCallback(() => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  }, []);

  // Get patrols that are visible in the current week (respecting filters)
  const patrolsInCurrentWeek = useMemo(() => {
    const weekStartMoment = moment(weekStart).startOf("day");
    const weekEndMoment = moment(weekEnd).endOf("day");

    return filteredPatrols.filter((patrol) => {
      const patrolStart = moment(patrol.StartTime);
      // A patrol is in this week if it starts within the week
      // This ensures we don't duplicate the same patrol multiple times
      // for overnight patrols that span into the next week
      return patrolStart.isSameOrAfter(weekStartMoment) && patrolStart.isSameOrBefore(weekEndMoment);
    });
  }, [filteredPatrols, weekStart, weekEnd]);

  // Unique guard names in the current week's patrols (for display in duplicate dialog)
  const uniqueGuardsInWeek = useMemo(() => {
    const seen = new Set();
    const guards = [];
    patrolsInCurrentWeek.forEach((p) => {
      if (!seen.has(p.ContactId)) {
        seen.add(p.ContactId);
        guards.push(p.ContactName);
      }
    });
    return guards.sort();
  }, [patrolsInCurrentWeek]);

  // Week labels for the confirmation dialog
  const sourceWeekLabel = useMemo(
    () => `${moment(weekStart).format("MMM D")} – ${moment(weekEnd).format("MMM D, YYYY")}`,
    [weekStart, weekEnd]
  );

  const targetWeekLabel = useMemo(
    () =>
      `${moment(weekStart).add(7, "days").format("MMM D")} – ${moment(weekEnd).add(7, "days").format("MMM D, YYYY")}`,
    [weekStart, weekEnd]
  );

  // Open the duplicate confirmation dialog
  const handleDuplicateClick = useCallback(() => {
    if (patrolsInCurrentWeek.length === 0) return;
    setShowDuplicateConfirm(true);
  }, [patrolsInCurrentWeek]);

  // Close the duplicate confirmation dialog
  const handleDuplicateCancel = useCallback(() => {
    setShowDuplicateConfirm(false);
  }, []);

  // Handle duplicating all visible patrols to next week
  const handleDuplicateToNextWeek = useCallback(() => {
    setShowDuplicateConfirm(false);
    if (patrolsInCurrentWeek.length === 0) return;

    // Create duplicated patrols with times shifted by 7 days
    // This handles overnight patrols correctly (e.g., Sunday 10PM to Monday 2AM
    // becomes next Sunday 10PM to next Monday 2AM)
    const duplicatedPatrols = patrolsInCurrentWeek.map((patrol) => ({
      ContactName: patrol.ContactName,
      ContactId: patrol.ContactId,
      CustomerName: patrol.CustomerName,
      CustomerId: patrol.CustomerId,
      AddressName: patrol.AddressName,
      AddressId: patrol.AddressId,
      StartTime: moment(patrol.StartTime).add(7, 'days').toDate(),
      EndTime: moment(patrol.EndTime).add(7, 'days').toDate(),
    }));

    onDuplicateWeek?.(duplicatedPatrols);
  }, [patrolsInCurrentWeek, onDuplicateWeek]);

  const handleSelectEvent = useCallback(
    (event) => {
      // Ignore clicks on break events
      if (event.isBreak) return;

      // When event is clicked, select that guard's shift for that day
      // Use originalStart for split events to ensure correct shift lookup
      if (event.resource) {
        const eventStart = event.originalStart || event.start;
        onEventClick?.(event.resource, eventStart); // Pass guardName and date
      }
    },
    [onEventClick]
  );

  // Handle edit button click on calendar event
  const handleEditClick = useCallback(
    (event) => {
      if (event.patrol) {
        onEditPatrol?.(event.patrol);
      }
    },
    [onEditPatrol]
  );

  // Custom event component wrapper to pass the edit handler, view mode, and mobile flag
  const EventComponent = useCallback(
    (props) => <CustomEvent {...props} onEditClick={handleEditClick} viewMode={viewMode} isMobile={isMobile} />,
    [handleEditClick, viewMode, isMobile]
  );

  // Create the WeekDayHeader component with mobile awareness
  const WeekDayHeader = useMemo(() => createWeekDayHeader(isMobile), [isMobile]);

  // Style events based on their shift color or break status
  const eventPropGetter = useCallback((event) => {
    // Break events get a ghost/transparent style
    if (event.isBreak) {
      return {
        style: {
          backgroundColor: "transparent",
          border: "1px dashed rgba(0, 0, 0, 0.2)",
          color: "rgba(0, 0, 0, 0.4)",
          borderRadius: "4px",
          padding: "2px 4px",
          fontSize: "0.75em",
          fontWeight: 400,
          fontStyle: "italic",
        },
      };
    }

    // Regular patrol events get shift colors
    const colors = SHIFT_COLORS[event.colorIndex ?? 0];
    return {
      style: {
        backgroundColor: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        color: colors.text,
        borderRadius: "4px",
        padding: "2px 4px",
        fontSize: "0.85em",
        fontWeight: 500,
      },
    };
  }, []);

  return (
    <Box sx={calendarStyles}>
      <Box sx={{ padding: 2 }}>
        {!hasRequiredSelection ? (
          // Placeholder when no selection is made
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "400px",
            }}
          >
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                Calendar View
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {viewMode === 'company'
                  ? 'Select a company to view scheduled patrols'
                  : 'Select a guard to view their schedule'}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "text.disabled",
                }}
              >
                {viewMode === 'company' ? 'No company selected' : 'No guard selected'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {/* Title Row */}
            <Box sx={{ marginBottom: 1.5 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ fontWeight: 600, marginBottom: 0.5 }}
              >
                {isMobile ? (
                  /* Mobile: Compact week display */
                  `${moment(weekStart).format("MMM D")} - ${moment(weekEnd).format("MMM D")}`
                ) : (
                  "Calendar View"
                )}
              </Typography>
              {!isMobile && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {viewMode === 'company' ? (
                    <>
                      Viewing all patrols for{" "}
                      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {selectedCompanyName}
                      </Box>
                      {selectedLocationName && (
                        <>
                          {" "}at location{" "}
                          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                            {selectedLocationName}
                          </Box>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Viewing all patrols for{" "}
                      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {selectedGuardName}
                      </Box>
                      {" "}across all companies
                    </>
                  )}
                  {" "}on the{" "}
                  <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
                    week of {moment(weekStart).format("MMM D")} to {moment(weekEnd).format("MMM D, YYYY")}
                  </Box>
                </Typography>
              )}
            </Box>

            {/* Controls Row - Navigation on left, Zoom on right */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 2,
                flexWrap: isMobile ? "wrap" : "nowrap",
                gap: isMobile ? 1 : 0,
              }}
            >
              {/* Left side - Navigation and Duplicate buttons */}
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {isMobile ? (
                  /* Mobile: Icon-only navigation buttons */
                  <>
                    <Tooltip title="Previous week">
                      <IconButton
                        color="primary"
                        onClick={handlePreviousWeek}
                        size="small"
                        sx={{ border: 1, borderColor: 'primary.main' }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Next week">
                      <IconButton
                        color="primary"
                        onClick={handleNextWeek}
                        size="small"
                        sx={{ border: 1, borderColor: 'primary.main' }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={patrolsInCurrentWeek.length === 0
                        ? "No patrols to duplicate"
                        : `Duplicate ${patrolsInCurrentWeek.length} patrol${patrolsInCurrentWeek.length !== 1 ? 's' : ''}`}
                    >
                      <span>
                        <IconButton
                          color="secondary"
                          onClick={handleDuplicateClick}
                          disabled={patrolsInCurrentWeek.length === 0}
                          size="small"
                          sx={{ border: 1, borderColor: 'secondary.main' }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  /* Desktop: Full button text */
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handlePreviousWeek}
                      startIcon={<ChevronLeftIcon />}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleNextWeek}
                      endIcon={<ChevronRightIcon />}
                    >
                      Next
                    </Button>
                    <Tooltip
                      title={patrolsInCurrentWeek.length === 0
                        ? "No patrols to duplicate this week"
                        : `Duplicate ${patrolsInCurrentWeek.length} patrol${patrolsInCurrentWeek.length !== 1 ? 's' : ''} to next week`}
                    >
                      <span>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleDuplicateClick}
                          startIcon={<ContentCopyIcon />}
                          disabled={patrolsInCurrentWeek.length === 0}
                        >
                          Duplicate To Next Week
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>

              {/* Right side - Zoom buttons */}
              <ButtonGroup
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiButton-root': {
                    minWidth: 36,
                    px: 1,
                  },
                }}
              >
                <Tooltip title="Zoom in" placement={isMobile ? "top" : "left"}>
                  <span>
                    <Button
                      onClick={handleZoomIn}
                      disabled={!canZoomIn}
                      aria-label="Zoom in"
                    >
                      <AddIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Zoom out" placement={isMobile ? "top" : "left"}>
                  <span>
                    <Button
                      onClick={handleZoomOut}
                      disabled={!canZoomOut}
                      aria-label="Zoom out"
                    >
                      <RemoveIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
              </ButtonGroup>
            </Box>

            {/* Empty state when no patrols visible this week */}
            {!hasPatrolsThisWeek ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px",
                  gap: 2,
                  py: 4,
                }}
              >
                <EventBusyIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                <Typography
                  variant="h6"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  No patrols scheduled
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", textAlign: "center", maxWidth: 300 }}
                >
                  There are no patrols scheduled for this week. Use the Add Patrol button in the top bar to create one.
                </Typography>
              </Box>
            ) : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventPropGetter}
                popup
                views={["week"]}
                defaultView="week"
                date={currentDate}
                onNavigate={setCurrentDate}
                toolbar={false}
                components={{
                  week: {
                    header: WeekDayHeader,
                  },
                  event: EventComponent,
                }}
              />
            )}
          </>
        )}
      </Box>

      {/* Duplicate week confirmation dialog */}
      <DuplicateWeekConfirmationDialog
        open={showDuplicateConfirm}
        patrolCount={patrolsInCurrentWeek.length}
        uniqueGuards={uniqueGuardsInWeek}
        sourceWeekLabel={sourceWeekLabel}
        targetWeekLabel={targetWeekLabel}
        onCancel={handleDuplicateCancel}
        onConfirm={handleDuplicateToNextWeek}
      />
    </Box>
  );
};

SchedulerCalendar.propTypes = {
  patrols: PropTypes.arrayOf(
    PropTypes.shape({
      ScheduleAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      ContactName: PropTypes.string.isRequired,
      ContactId: PropTypes.number,
      CustomerName: PropTypes.string,
      CustomerId: PropTypes.number,
      AddressName: PropTypes.string,
      AddressId: PropTypes.number,
      StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
      EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    })
  ),
  shifts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      ContactName: PropTypes.string,
      patrols: PropTypes.array,
    })
  ),
  viewMode: PropTypes.oneOf(["company", "guard"]),
  selectedCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedLocationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedGuardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedCompanyName: PropTypes.string,
  selectedLocationName: PropTypes.string,
  selectedGuardName: PropTypes.string,
  onWeekChange: PropTypes.func,
  onEventClick: PropTypes.func,
  onEditPatrol: PropTypes.func,
  onRegisterNavigate: PropTypes.func,
  onDuplicateWeek: PropTypes.func,
  isMobile: PropTypes.bool,
};

// Memoize component to prevent unnecessary re-renders when parent state changes
export default memo(SchedulerCalendar);
